import { PromotionType } from "@sellar-io/api-v2/types";
import { InputText } from "@sellar-io/components";
import { useState } from "react";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../components/modal/Dialog";
import { z } from "zod";
import { Alert } from "../../components/alert/Alert";
import { useForm, useStore } from "@tanstack/react-form";
import { InputLabel, InputContainer, InputSelect } from "@sellar-io/components";
import { usePromotionCreate } from "../../hooks/api/v2/promotion/usePromotionCreate";
import { pluralize } from "@sellar-io/common";
import { Switch, InputDate } from "@sellar-io/components";
import dayjs from "dayjs";
import { apiV2AssertError } from "../../hooks/api/v2/apiV2Request";
import { Button, H4, Icon } from "@sellar-io/components";
import isBetween from "dayjs/plugin/isBetween";
import { usePromotionDelete } from "../../hooks/api/v2/promotion/usePromotionDelete";
import { usePromotionList } from "../../hooks/api/v2/promotion/usePromotionList";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { SpinnerLegacy } from "../../components";
import {
  DataButton,
  H5,
  Anchor,
  Card,
  ListItemIconTile,
} from "@sellar-io/components";
import { BadgeGreen, BadgeGrey } from "../../components/badge/Badge";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { useNavigate } from "react-router";
import * as ROUTES from "~/constants/routes";
import { NavigationBarMenu } from "../../components/navigation/NavigationBar";
import {
  PromotionFormSchema,
  defaultPromotionFormData,
  getDatesOverlap,
  getRewardRuleDescription,
} from "./GlobalPromotionsTab.shared";

dayjs.extend(isBetween);

type GlobalPromotionType = Pick<
  PromotionType,
  "id" | "startAt" | "endAt" | "isActive" | "rewardRule" | "name"
>;

type GlobalPromotionsTabProps = {
  promotionRoute?: typeof ROUTES.PROMOTION | typeof ROUTES.PROMOTION_STANDALONE;
};

export const GlobalPromotionsTab = ({
  promotionRoute = ROUTES.PROMOTION,
}: GlobalPromotionsTabProps = {}) => {
  const { business } = useSellarCurrentBusiness();
  const supplierId = business?.Supplier?.id;

  const promotionsQuery = usePromotionList(
    {
      query: supplierId
        ? [{ type: "SupplierId", modifier: "EQUALS", value: supplierId }]
        : [],
    },
    { enabled: Boolean(supplierId) },
  );

  const promotions = promotionsQuery.data?.pages[0] ?? [];

  if (promotionsQuery.isPending) {
    return <SpinnerLegacy />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <H5>Promotions</H5>
          <span className="text-zinc-500">
            Create promotions to offer rewards to customers when they meet a
            certain spend threshold
          </span>
        </div>
        <CreateGlobalPromotionButton
          promotions={promotions}
          promotionRoute={promotionRoute}
        />
      </div>
      <GlobalPromotionList
        promotions={promotions}
        supplierId={supplierId}
        promotionRoute={promotionRoute}
      />
    </div>
  );
};

export const GlobalPromotionsPage = () => {
  return (
    <>
      <NavigationBarMenu title="Promotions" borderBottom={false} />
      <div className="mx-auto flex max-w-230 items-center gap-2">
        <div className="mt-2 mb-24 flex w-full flex-col gap-4 px-4">
          <GlobalPromotionsTab promotionRoute={ROUTES.PROMOTION_STANDALONE} />
        </div>
      </div>
    </>
  );
};

const GlobalPromotionList = ({
  promotions,
  supplierId,
  promotionRoute,
}: {
  promotions: Array<GlobalPromotionType>;
  supplierId: number | undefined;
  promotionRoute: typeof ROUTES.PROMOTION | typeof ROUTES.PROMOTION_STANDALONE;
}) => {
  if (!supplierId) return null;

  if (promotions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-8">
        <H4 className="text-zinc-500">No promotions yet</H4>
        <p className="text-sm text-zinc-400">
          Click 'New' to create your first promotion. For more information or
          help setting up a promotion, visit our{" "}
          <Anchor
            className="inline-flex items-center gap-1 text-blue-600 underline"
            href="https://intercom.help/sellario/en/articles/15842115-supplier-guide-promotions"
            target="_blank"
            rel="noopener noreferrer"
          >
            help centre
            <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
          </Anchor>
          .
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {promotions.map((promotion) => (
        <GlobalPromotionItem
          key={promotion.id}
          promotion={promotion}
          promotionRoute={promotionRoute}
        />
      ))}
    </div>
  );
};

const GlobalPromotionItem = ({
  promotion,
  promotionRoute,
}: {
  promotion: GlobalPromotionType;
  promotionRoute: typeof ROUTES.PROMOTION | typeof ROUTES.PROMOTION_STANDALONE;
}) => {
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const navigate = useNavigate();
  const startDate = promotion.startAt
    ? dayjs(promotion.startAt).format("Do MMMM YYYY")
    : "";
  const endDate = promotion.endAt
    ? dayjs(promotion.endAt).format("Do MMMM YYYY")
    : "";

  const hasSetValidDates = startDate && endDate;

  return (
    <>
      <Card
        onClick={() => {
          navigate(promotionRoute.href({ promotionId: promotion.id }));
        }}
        className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
      >
        <ListItemIconTile icon="PARTY_POPPER" />
        <div className="flex flex-1 flex-col">
          <p className="font-semibold">{promotion.name}</p>
          <p className="text-sm text-zinc-600">
            {hasSetValidDates && <span>{`${startDate} - ${endDate}`}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <div>
            {promotion.isActive ? (
              <BadgeGreen icon="CHECK_CIRCLE">Running</BadgeGreen>
            ) : (
              <BadgeGrey icon="DOTS_HORIZONTAL_CIRCLE">Inactive</BadgeGrey>
            )}
          </div>
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            <Dropdown>
              <DropdownButton>
                <Button size={"small"} color={"transparent"}>
                  <Icon icon={"PENCIL"} className="text-black" size={0.7} />
                </Button>
              </DropdownButton>
              <DropdownItems>
                <DropdownItem
                  className="border-b border-zinc-200 text-sm"
                  onClick={() =>
                    navigate(promotionRoute.href({ promotionId: promotion.id }))
                  }
                >
                  Edit
                </DropdownItem>

                <DropdownItem
                  className="border-b border-zinc-200 text-sm text-red-700"
                  onClick={() => setOpenDeleteDialog(true)}
                >
                  Delete
                </DropdownItem>
              </DropdownItems>
            </Dropdown>
          </div>
        </div>
      </Card>
      <DeleteGlobalPromotionDialog
        promotion={promotion}
        open={openDeleteDialog}
        onDismiss={() => setOpenDeleteDialog(false)}
      />
    </>
  );
};

export type PromotionFormSchemaType = z.infer<typeof PromotionFormSchema>;

const CreateGlobalPromotionButton = ({
  promotions,
  promotionRoute,
}: {
  promotions: Array<GlobalPromotionType>;
  promotionRoute: typeof ROUTES.PROMOTION | typeof ROUTES.PROMOTION_STANDALONE;
}) => {
  const { business } = useSellarCurrentBusiness();
  const navigate = useNavigate();
  const supplierId = business?.Supplier?.id;
  const [promotionDialogActive, setPromotionDialogActive] = useState(false);
  const promotionCreateMutation = usePromotionCreate();
  const [errorMessage, setErrorMessage] = useState("");
  const [
    overlappingPromotionsByDateCount,
    setOverlappingPromotionsByDateCount,
  ] = useState(0);

  const handleDismiss = () => {
    setPromotionDialogActive(false);
    setOverlappingPromotionsByDateCount(0);
    setErrorMessage("");
  };

  const handleCreatePromotion = (data: PromotionFormSchemaType) => {
    setErrorMessage("");
    promotionCreateMutation.mutate(
      {
        name: data.name,
        startAt: dayjs(data.startAt).startOf("day").toDate(),
        endAt: dayjs(data.endAt).startOf("day").toDate(),
        rewardRule: data.rewardRule as PromotionType["rewardRule"],
        isActive: data.isActive,
        // No PriceTierId or PromotionCampaignId needed — the backend detects
        // the global case (null PriceTierId) and finds-or-creates the
        // supplier-level PromotionCampaign automatically.
      },
      {
        onSuccess: (data) => {
          handleDismiss();
          navigate(promotionRoute.href({ promotionId: data.id }));
        },
        onError: (error) => {
          const message = apiV2AssertError(error)?.response?.data.message ?? "";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleMonitorDateOverlap = ({
    startAt,
    endAt,
  }: {
    startAt: string;
    endAt: string;
  }) => {
    const newStartAt = startAt ? dayjs(startAt).startOf("day").toDate() : null;
    const newEndAt = endAt ? dayjs(endAt).startOf("day").toDate() : null;

    const overlappingPromotions = promotions
      .map((promotion) => ({
        startAt: dayjs(promotion.startAt).startOf("day").toDate(),
        endAt: dayjs(promotion.endAt).endOf("day").toDate(),
      }))
      .filter((promotionDates) =>
        getDatesOverlap({
          startDateA: newStartAt,
          endDateA: newEndAt,
          startDateB: promotionDates.startAt,
          endDateB: promotionDates.endAt,
        }),
      );

    setOverlappingPromotionsByDateCount(overlappingPromotions.length);
  };

  if (!supplierId) return null;

  return (
    <>
      <DataButton
        className="flex items-center gap-1"
        border={true}
        onClick={() => setPromotionDialogActive(true)}
      >
        <Icon icon="PLUS" />
        New
      </DataButton>
      <Dialog active={promotionDialogActive} onDismiss={handleDismiss}>
        <DialogNavigation
          title="Create new promotion"
          description="Use the fields below to set up your promotion"
          leftIcon="CHEVRON_DOWN"
          onClickLeftIcon={handleDismiss}
        />
        <DialogContent>
          <PromotionForm
            mode="create"
            initialData={defaultPromotionFormData}
            errorMessage={errorMessage}
            metadata={{ overlappingPromotionsByDateCount }}
            isSubmitting={promotionCreateMutation.isPending}
            onChangeDates={handleMonitorDateOverlap}
            onSubmit={handleCreatePromotion}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

type DeleteGlobalPromotionDialogProps = {
  open: boolean;
  promotion: GlobalPromotionType;
  onDismiss: () => void;
};

const DeleteGlobalPromotionDialog = ({
  open,
  promotion,
  onDismiss,
}: DeleteGlobalPromotionDialogProps) => {
  const [errorMessage, setErrorMessage] = useState("");
  const deleteMutation = usePromotionDelete(promotion.id);

  const handleDelete = () => {
    setErrorMessage("");
    deleteMutation.mutate(undefined, {
      onSuccess: () => handleDismiss(),
      onError: (error) => {
        const message = apiV2AssertError(error)?.response?.data.message ?? "";
        setErrorMessage(message);
      },
    });
  };

  const handleDismiss = () => {
    setErrorMessage("");
    onDismiss();
  };

  const isDeleting = deleteMutation.isPending;

  return (
    <Dialog active={open} onDismiss={handleDismiss}>
      <DialogNavigation
        title={`Delete ${promotion.name}`}
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={handleDismiss}
      />
      <DialogContent>
        <div className="flex flex-col gap-4">
          {Boolean(errorMessage) && (
            <Alert type="error" message={errorMessage} className="w-full" />
          )}
          <p>
            Are you sure you want to delete <strong>{promotion.name}</strong>?
          </p>
        </div>
      </DialogContent>
      <DialogCTA>
        <Button color="secondary" disabled={isDeleting} onClick={handleDismiss}>
          Cancel
        </Button>
        <Button
          disabled={isDeleting}
          loading={isDeleting}
          color="red"
          onClick={handleDelete}
        >
          Delete
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

type PromotionFormProps = {
  mode: "create" | "update";
  initialData: PromotionFormSchemaType;
  errorMessage: string;
  metadata: { overlappingPromotionsByDateCount: number };
  isSubmitting: boolean;
  onChangeDates: (_dates: { startAt: string; endAt: string }) => void;
  onSubmit: (_formData: PromotionFormSchemaType) => void;
};

export const PromotionForm = ({
  initialData,
  isSubmitting,
  errorMessage,
  metadata,
  mode,
  onSubmit,
  onChangeDates,
}: PromotionFormProps) => {
  const form = useForm({
    defaultValues: initialData,
    validators: {
      onChange: PromotionFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(PromotionFormSchema.parse(value));
    },
  });

  const startAt = useStore(form.store, (state) => state.values.startAt);
  const endAt = useStore(form.store, (state) => state.values.endAt);

  const isFormDisabled = isSubmitting;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      className="flex flex-col gap-4"
    >
      {errorMessage && (
        <Alert type="error" message={errorMessage} className="w-full" />
      )}
      {metadata.overlappingPromotionsByDateCount > 0 && (
        <Alert
          type="warning"
          message={`This promotion overlaps with ${metadata.overlappingPromotionsByDateCount} existing ${pluralize("promotion", metadata.overlappingPromotionsByDateCount)}`}
          className="w-full"
        />
      )}
      <form.Field name="name">
        {({ state, handleChange, handleBlur }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>Name</InputLabel>
              <p className="text-sm text-zinc-500">
                Your promotion's name will be shown to customers on your
                storefront and at checkout
              </p>
              <InputContainer>
                <InputText
                  placeholder="Summer Sale"
                  value={state.value}
                  disabled={isFormDisabled}
                  onBlur={handleBlur}
                  onChange={(event) => handleChange(event.target.value)}
                />
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="startAt">
        {({ state, handleChange, handleBlur }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>Start date</InputLabel>
              <p className="text-sm text-zinc-500">
                When you want this promotion to come into effect
              </p>
              <InputContainer>
                <InputDate
                  value={state.value}
                  disabled={isFormDisabled}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    handleChange(event.target.value);
                    onChangeDates({ startAt: event.target.value, endAt });
                  }}
                />
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="endAt">
        {({ state, handleChange, handleBlur }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>End date</InputLabel>
              <p className="text-sm text-zinc-500">
                When you want this promotion to end
              </p>
              <InputContainer>
                <InputDate
                  value={state.value}
                  disabled={isFormDisabled}
                  onBlur={handleBlur}
                  onChange={(event) => {
                    handleChange(event.target.value);
                    onChangeDates({ startAt, endAt: event.target.value });
                  }}
                />
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="rewardRule">
        {({ state, handleChange }) => {
          return (
            <div className="flex flex-col gap-1">
              <InputLabel>Reward rule</InputLabel>
              <p className="text-sm text-zinc-500">
                You can choose between letting qualifying customers choose just
                one reward, or as many as you have listed
              </p>
              <InputContainer>
                <InputSelect
                  disabled={isFormDisabled}
                  value={state.value}
                  onChange={(event) => handleChange(event.target.value)}
                >
                  <option value="">Select a reward rule</option>
                  <option value="all_of">
                    {getRewardRuleDescription("all_of")}
                  </option>
                  <option value="one_of">
                    {getRewardRuleDescription("one_of")}
                  </option>
                </InputSelect>
              </InputContainer>
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <form.Field name="isActive">
        {({ state, handleChange }) => {
          return (
            <div className="flex items-center justify-between gap-1">
              <InputLabel>Activate this promotion</InputLabel>
              <Switch
                checked={state.value}
                disabled={isFormDisabled}
                onChange={(checked) => handleChange(checked)}
              />
              <FieldError errorMessage={state.meta.errors[0]?.message} />
            </div>
          );
        }}
      </form.Field>
      <div className="flex justify-end gap-4">
        <Button disabled={isFormDisabled} loading={isSubmitting} type="submit">
          {mode === "create" && "Create"}
          {mode === "update" && "Update"}
        </Button>
      </div>
    </form>
  );
};

const FieldError = ({ errorMessage }: { errorMessage: string | undefined }) => {
  if (!errorMessage) return null;
  return (
    <span role="alert" className="text-sm text-red-700">
      {errorMessage}
    </span>
  );
};
